import {
  DEFAULT_ELEVENLABS_API_KEY,
  GEMINI_API_KEY,
} from "../plan/dayplan/components/dailyStep/mediaGeneratorConfig";
import { createOpenAIChatCompletion } from "../../../services/openaiFunctions";
import { PracticeType, inferPracticeType } from "../../../types/Habit";

// ─── Legacy types (kept for Root.tsx compatibility) ───────────────────────────

export type OnboardingData = {
  communityName: string;
  category: string;
  communityDescription: string;
  targetAudience: string;
  mainChallenge: string;
  transformation: string;
  websiteUrl: string;
  socialLinks: string;
  additionalContext: string;
  writingStyle: string;
  visualStyle: string;
};

export type GeneratedDraft = {
  title: string;
  description: string;
  shortDescription: string;
  categoryName: string;
  tags: string[];
  writingStyle: string;
  visualStyle: string;
  authorDescription: string;
  aiGuidelines: string;
  primaryColor?: string;
  secondaryColor?: string;
  logoURL?: string;
  lang?: string;
  // Visual preset for the app UI (see ThePath/assets/stylePacks.ts). The app
  // falls back to fontTheme mapping, then "classic", when missing or unknown.
  stylePack?: "classic" | "energetic" | "zen" | "editorial" | "calmPower";
};

// ─── New types ────────────────────────────────────────────────────────────────

export type ContentSources = {
  websiteUrl?: string;
  pastedContent?: string;
  uploadedFiles?: UploadedFile[];
};

export type UploadedFile = {
  name: string;
  mimeType: string;
  /** raw File object for PDF/binary uploads */
  file?: File;
  /** plain text content for .txt / .md */
  text?: string;
};

export type AuthorDraft = {
  fullName: string;
  description: string;
  shortDescription: string;
  aiGuidelines: string;
  tags: string[];
  voiceId?: string;
  agentId?: string;
  profilePhotoURL?: string;
};

export type SubactionDraft = {
  name: string;
  description: string;
  actionType: "exercise" | "diet" | "meditation" | "sleep" | "custom";
  /** AI prompt used to generate personalized guidance for this subaction */
  aiPrompt?: string;
  duration?: number;
  intensityLevel?: "low" | "medium" | "high";
  /** System prompt used by the assistant chat inside this subaction */
  reflectionPrompt?: string;
};

export type ActionDraft = {
  name: string;
  description: string;
  actionType: "exercise" | "diet" | "meditation" | "sleep" | "custom";
  duration?: number;
  goal?: string;
  subactions: SubactionDraft[];
};

export type HabitDraft = {
  habitName: string;
  habitDescription: string;
  targetFrequency: string;
  /** Practice vertical the AI classified this habit as (numeric PracticeType). */
  practiceType: PracticeType;
  actions: ActionDraft[];
};

export type QuoteDraft = {
  quote: string;
  authorName: string;
};

export type OnboardingQuestionDraft = {
  id: string;
  question: string;
  type: "SINGLE_SELECTION" | "MULTIPLE_SELECTION" | "TEXT";
  options?: { description: string; value: string }[];
  placeholder?: string;
};

export type QuestionnaireBeforeAfterDraft = {
  before: string[];
  after: string[];
};

export type QuestionnaireCommitDraft = {
  greeting: string;
  commitments: string[];
  subcopy: string;
};

export type QuestionnaireReviewDraft = {
  name: string;
  text: string;
  stars: 1 | 2 | 3 | 4 | 5;
};

export type QuestionnaireDraft = {
  questions: OnboardingQuestionDraft[];
  aiPrompt: string;
  beforeAfter: QuestionnaireBeforeAfterDraft;
  commit: QuestionnaireCommitDraft;
  reviews: QuestionnaireReviewDraft[];
};

export const sanitizeStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter(Boolean)
    : [];

const sanitizeQuestionnaireDraft = (
  payload: Partial<QuestionnaireDraft> | null | undefined
): QuestionnaireDraft => {
  const rawQuestions = Array.isArray(payload?.questions) ? payload?.questions : [];
  const questions = rawQuestions
    .map((question, index) => {
      const rawType = typeof question?.type === "string" ? question.type.trim().toUpperCase() : "TEXT";
      const type: OnboardingQuestionDraft["type"] =
        rawType === "SINGLE_SELECTION" ||
        rawType === "MULTIPLE_SELECTION" ||
        rawType === "TEXT"
          ? rawType
          : "TEXT";

      const options =
        type === "TEXT"
          ? undefined
          : (Array.isArray(question?.options) ? question.options : [])
              .map((option, optionIndex) => ({
                description:
                  typeof option?.description === "string" && option.description.trim()
                    ? option.description.trim()
                    : `Option ${optionIndex + 1}`,
                value:
                  typeof option?.value === "string" && option.value.trim()
                    ? option.value.trim()
                    : `option_${index + 1}_${optionIndex + 1}`,
              }))
              .filter((option) => option.description);

      return {
        id:
          typeof question?.id === "string" && question.id.trim()
            ? question.id.trim()
            : `q${index + 1}`,
        question:
          typeof question?.question === "string" && question.question.trim()
            ? question.question.trim()
            : `Question ${index + 1}`,
        type,
        ...(options?.length ? { options } : {}),
        ...(typeof question?.placeholder === "string" && question.placeholder.trim()
          ? { placeholder: question.placeholder.trim() }
          : {}),
      };
    })
    .filter((question) => question.question);

  const reviews = (Array.isArray(payload?.reviews) ? payload.reviews : [])
    .map((review, index) => {
      const numericStars = Number(review?.stars);
      const safeStars = Number.isInteger(numericStars)
        ? Math.max(1, Math.min(5, numericStars))
        : 5;

      return {
        name:
          typeof review?.name === "string" && review.name.trim()
            ? review.name.trim()
            : `Member ${index + 1}`,
        text:
          typeof review?.text === "string" && review.text.trim()
            ? review.text.trim()
            : "",
        stars: safeStars as 1 | 2 | 3 | 4 | 5,
      };
    })
    .filter((review) => review.text);

  return {
    questions,
    aiPrompt:
      typeof payload?.aiPrompt === "string" && payload.aiPrompt.trim()
        ? payload.aiPrompt.trim()
        : "",
    beforeAfter: {
      before: sanitizeStringArray(payload?.beforeAfter?.before),
      after: sanitizeStringArray(payload?.beforeAfter?.after),
    },
    commit: {
      greeting:
        typeof payload?.commit?.greeting === "string" && payload.commit.greeting.trim()
          ? payload.commit.greeting.trim()
          : "",
      commitments: sanitizeStringArray(payload?.commit?.commitments),
      subcopy:
        typeof payload?.commit?.subcopy === "string" && payload.commit.subcopy.trim()
          ? payload.commit.subcopy.trim()
          : "",
    },
    reviews,
  };
};

export type PlanDraftStepList = {
  id: string;
  title?: string;
  ordered?: boolean;
  items: string[];
};

export type PlanDraftStepCallout = {
  id: string;
  tone?: "info" | "warning" | "tip" | "note";
  title?: string;
  text: string;
};

export type PlanDraftStep = {
  title: string;
  subtitle: string;
  sectionTitle: string;
  description: string;
  position: number;
  learningStyle: "NARRATIVE" | "CAUSAL" | "SOCRATIC" | "MODEL" | "ACTION";
  text?: string;
  lists?: PlanDraftStepList[];
  callouts?: PlanDraftStepCallout[];
};

export type PlanDraftDay = {
  title: string;
  description: string;
  steps: PlanDraftStep[];
};

export type PlanDraft = {
  title: string;
  shortDescription: string;
  description: string;
  level: string;
  tags: string[];
  dayPlans: PlanDraftDay[];
};

export type StarterContent = {
  author: AuthorDraft;
  habits: HabitDraft[];
  plans: PlanDraft[];
  quotes: QuoteDraft[];
  questionnaire: QuestionnaireDraft;
};

export type FullCommunityContent = {
  community: GeneratedDraft;
  author: AuthorDraft;
  habits: HabitDraft[];
  plans: PlanDraft[];
  quotes: QuoteDraft[];
  questionnaire: QuestionnaireDraft;
};

export type ReflectionPromptPackContext = {
  community?: {
    title?: string;
    description?: string;
    shortDescription?: string;
    categoryName?: string;
    tags?: string[];
    writingStyle?: string;
    visualStyle?: string;
  } | null;
  author?: {
    fullName?: string;
    description?: string;
    shortDescription?: string;
    aiGuidelines?: string;
    tags?: string[];
  } | null;
  habit?: {
    habitName?: string;
    habitDescription?: string;
    targetFrequency?: string;
  } | null;
  action?: {
    name?: string;
    description?: string;
    actionType?: string;
    duration?: number;
    goal?: string;
    tags?: string[];
  } | null;
  subaction?: {
    name?: string;
    description?: string;
    actionType?: string;
    duration?: number;
    intensityLevel?: string;
    notes?: string;
    primaryIntent?: string;
    secondaryBenefits?: string;
    bodyRegions?: string[];
    movementQualities?: string[];
    userUseCases?: string[];
    tags?: string[];
  } | null;
};

export const COMMUNITY_CATEGORIES = [
  "Fitness & Health",
  "Business",
  "Personal Development",
  "Finance",
  "Relationships",
  "Education",
  "Technology",
  "Creativity",
  "Spirituality",
  "Other",
];

// ─── Gemini REST helpers ──────────────────────────────────────────────────────

const GEMINI_MODEL = "gemini-3.5-flash";
const GEMINI_BASE = "https://generativelanguage.googleapis.com/v1beta";
const ELEVENLABS_BASE = "https://api.elevenlabs.io/v1";
const OPENAI_AGENT_MODEL = "gpt-5-mini";
// LLM that powers the created ElevenLabs agent at runtime. Must be one of the
// model ids ElevenLabs Agents natively supports.
const ELEVENLABS_AGENT_LLM = "gemini-3.5-flash";
// Agent-specific v3 model with expressive speech across 70+ languages.
const ELEVENLABS_AGENT_TTS_MODEL = "eleven_v3_conversational";
// ElevenLabs rejects non-English agents on any TTS model other than
// eleven_turbo_v2_5 / eleven_flash_v2_5 (400 invalid_parameters).
const ELEVENLABS_AGENT_TTS_MODEL_NON_EN = "eleven_turbo_v2_5";

// URL context tool — tells Gemini to fetch and read any URLs in the prompt
const URL_CONTEXT_TOOL = { urlContext: {} };

/**
 * Convert a File to inline base64 data for Gemini (works in-browser, no CORS).
 */
async function fileToInlinePart(file: File): Promise<any> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  const base64 = btoa(binary);
  return {
    inlineData: {
      mimeType: file.type || "application/pdf",
      data: base64,
    },
  };
}

/**
 * Fallback: call OpenAI when Gemini is unavailable.
 * Binary/inlineData parts are skipped — only text parts are forwarded.
 */
// Turn a thrown value into a human-readable reason. Firebase callable failures
// often arrive as a bare "internal" with the real cause only in the function
// logs, so we surface the error code and any details the backend attached
// instead of letting an opaque "internal" reach the UI.
export function describeError(err: any): string {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;

  const code: string = typeof err.code === "string" ? err.code : "";
  const message: string =
    typeof err.message === "string" ? err.message.trim() : "";
  const details =
    err.details == null
      ? ""
      : typeof err.details === "string"
        ? err.details
        : JSON.stringify(err.details);

  const FRIENDLY: Record<string, string> = {
    internal:
      "The AI service returned an internal error, usually because it was momentarily overloaded. Please try again in a moment.",
    "deadline-exceeded": "The AI request timed out. Please try again.",
    unavailable:
      "The AI service was temporarily unavailable. Please try again.",
    "resource-exhausted":
      "The AI service hit a rate or quota limit. Please wait a moment and try again.",
    unauthenticated: "Your session expired. Please sign in again.",
    "permission-denied": "You do not have permission to run this.",
  };

  const normalizedCode = code.replace(/^functions\//, "");
  const friendly = FRIENDLY[normalizedCode];

  // A bare "internal" message carries no information; prefer the code-based text.
  const base =
    friendly ||
    (message && message.toLowerCase() !== "internal" ? message : "") ||
    "Unexpected error";

  const suffix: string[] = [];
  if (code) suffix.push(`code: ${code}`);
  if (details && details !== message) suffix.push(details);
  return suffix.length ? `${base} (${suffix.join("; ")})` : base;
}

async function callOpenAIFallback(
  parts: any[],
  responseMimeType: string | null
): Promise<string> {
  const userContent = parts
    .filter((p) => typeof p.text === "string" && p.text.trim())
    .map((p) => p.text as string)
    .join("\n\n");

  const response = await createOpenAIChatCompletion<any>(
    {
      model: OPENAI_AGENT_MODEL,
      ...(responseMimeType === "application/json"
        ? { response_format: { type: "json_object" } }
        : {}),
      messages: [
        {
          role: "user",
          content: userContent || "Generate reasonable defaults for a general online community.",
        },
      ],
    },
    300000 // 5-minute timeout — large generations can take a while
  );

  return response?.choices?.[0]?.message?.content ?? "";
}

/**
 * Call Gemini generateContent.
 * Parts can be text strings, inlineData objects, or fileData objects.
 * Falls back to OpenAI on 503 (high demand) or 429 (rate limit).
 */
export async function callGemini(
  parts: any[],
  maxOutputTokens = 4096,
  useUrlContext = false,
  responseMimeType: string | null = "application/json",
  overrides?: {
    temperature?: number;
    model?: string;
    disableFallback?: boolean;
  }
): Promise<string> {
  const body: any = {
    contents: [{ role: "user", parts }],
    generationConfig: {
      temperature: overrides?.temperature ?? 0.7,
      maxOutputTokens,
      // responseMimeType cannot be combined with tools
      ...(useUrlContext || !responseMimeType ? {} : { responseMimeType }),
      // Disable thinking — saves tokens and cost for structured JSON generation
      thinkingConfig: { thinkingBudget: 0 },
    },
  };

  if (useUrlContext) {
    body.tools = [URL_CONTEXT_TOOL];
  }

  // A missing key would otherwise surface as an opaque Gemini 400. Fall back to
  // OpenAI when allowed, or say plainly that the key is not configured.
  if (!GEMINI_API_KEY) {
    if (!overrides?.disableFallback) {
      console.warn(
        "REACT_APP_GEMINI_API_KEY is not set — calling OpenAI directly."
      );
      return callOpenAIFallback(parts, responseMimeType);
    }
    throw new Error(
      "Gemini API key is not configured (REACT_APP_GEMINI_API_KEY)."
    );
  }

  const model = overrides?.model ?? GEMINI_MODEL;
  const res = await fetch(
    `${GEMINI_BASE}/models/${model}:generateContent?key=${GEMINI_API_KEY}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );

  if (!res.ok) {
    const errText = await res.text().catch(() => res.statusText);
    const isTransient =
      res.status === 503 ||
      res.status === 429 ||
      res.status === 504 ||
      errText.includes("DEADLINE_EXCEEDED");
    if (isTransient && !overrides?.disableFallback) {
      console.warn(`Gemini ${res.status} (${res.statusText}) — falling back to OpenAI`);
      try {
        return await callOpenAIFallback(parts, responseMimeType);
      } catch (fallbackErr) {
        // The fallback's bare "internal" hid the real cause. Name both.
        throw new Error(
          `AI generation failed: Gemini was overloaded (${res.status}) and the OpenAI fallback also failed: ${describeError(fallbackErr)}`
        );
      }
    }
    throw new Error(`Gemini API error ${res.status}: ${errText}`);
  }

  const json = await res.json();

  // When urlContext tool is active, Gemini returns multiple parts
  // (grounding metadata, text, etc.) — find the text part that contains JSON
  const responseParts: any[] = json.candidates?.[0]?.content?.parts ?? [];
  const textPart = responseParts.find((p: any) => typeof p.text === "string" && p.text.trim().length > 0);
  return textPart?.text ?? "";
}

export function parseJSON<T>(text: string): T {
  // Strip markdown code fences if Gemini wraps the JSON
  const clean = text.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/, "").trim();
  const match = clean.match(/[\[{][\s\S]*[\]}]/);
  if (!match) throw new Error("Could not parse Gemini response as JSON");
  return JSON.parse(match[0]) as T;
}

const cleanTextResponse = (text: string): string =>
  text
    .replace(/^```(?:markdown|md|text)?\s*/i, "")
    .replace(/\s*```$/, "")
    .trim();

const isValidAssistantPrompt = (text: string): boolean => {
  const normalized = text.trim();

  if (!/^You are\b/i.test(normalized)) return false;
  if (/^As a\b/i.test(normalized)) return false;
  if (/\bI will help you\b/i.test(text)) return false;
  if (/\bI can help\b/i.test(text)) return false;
  if (/\bfoolish question\b/i.test(text)) return false;
  if (/\bPlease create:\b/i.test(text)) return false;
  if (/\bBased on the following information:\b/i.test(text)) return false;

  return true;
};

const formatPromptContextValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    const items = value
      .map((item) => (typeof item === "string" ? item.trim() : `${item ?? ""}`.trim()))
      .filter(Boolean);
    return items.length ? items.join(", ") : "Not provided";
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "Not provided";
  }

  if (typeof value === "string") {
    return value.trim() || "Not provided";
  }

  return "Not provided";
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getInternalContextLabels = (
  context: ReflectionPromptPackContext
): string[] =>
  [
    context.community?.title,
    context.habit?.habitName,
    context.action?.name,
    context.subaction?.name,
  ]
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter((value) => value.length > 3);

const promptMentionsInternalLabels = (
  text: string,
  context: ReflectionPromptPackContext
): boolean =>
  getInternalContextLabels(context).some((label) => {
    const regex = new RegExp(escapeRegExp(label), "i");
    return regex.test(text);
  });

const promptUsesAuthorVoice = (
  text: string,
  context: ReflectionPromptPackContext
): boolean => {
  const authorName =
    typeof context.author?.fullName === "string"
      ? context.author.fullName.trim()
      : "";

  if (!authorName) return true;

  const opening = text.slice(0, 280);
  return new RegExp(escapeRegExp(authorName), "i").test(opening);
};

const promptFeelsGenericOrAssumptive = (text: string): boolean =>
  /\bYou are a (?:reflective|reflection|supportive)?\s*AI assistant\b/i.test(text) ||
  /\bYou are a Reflection Chat assistant\b/i.test(text) ||
  /\bYou are a Subaction Chat assistant\b/i.test(text) ||
  /\buser has just\b/i.test(text) ||
  /\bhas just tried\b/i.test(text) ||
  /\bjust tried\b/i.test(text) ||
  /\brecent attempt or completion\b/i.test(text) ||
  /\bcompleted or attempted\b/i.test(text);

// Shared context block fed to every prompt-pack generator (the system prompt
// and the conversation starters), so both read from the same Community, Author,
// Habit, Action, and Subaction signal.
const buildPromptPackContextBlock = (
  context: ReflectionPromptPackContext
): string => `Community title: ${formatPromptContextValue(context.community?.title)}
Community description: ${formatPromptContextValue(context.community?.description)}
Community short description: ${formatPromptContextValue(context.community?.shortDescription)}
Community category: ${formatPromptContextValue(context.community?.categoryName)}
Community tags: ${formatPromptContextValue(context.community?.tags)}
Community writing style: ${formatPromptContextValue(context.community?.writingStyle)}
Community visual style: ${formatPromptContextValue(context.community?.visualStyle)}

Author name: ${formatPromptContextValue(context.author?.fullName)}
Author description: ${formatPromptContextValue(context.author?.description)}
Author short description: ${formatPromptContextValue(context.author?.shortDescription)}
Author AI guidelines: ${formatPromptContextValue(context.author?.aiGuidelines)}
Author tags: ${formatPromptContextValue(context.author?.tags)}

Habit name: ${formatPromptContextValue(context.habit?.habitName)}
Habit description: ${formatPromptContextValue(context.habit?.habitDescription)}
Habit target frequency: ${formatPromptContextValue(context.habit?.targetFrequency)}

Action name: ${formatPromptContextValue(context.action?.name)}
Action description: ${formatPromptContextValue(context.action?.description)}
Action type: ${formatPromptContextValue(context.action?.actionType)}
Action duration: ${formatPromptContextValue(context.action?.duration)}
Action goal: ${formatPromptContextValue(context.action?.goal)}
Action tags: ${formatPromptContextValue(context.action?.tags)}

Subaction name: ${formatPromptContextValue(context.subaction?.name)}
Subaction description: ${formatPromptContextValue(context.subaction?.description)}
Subaction type: ${formatPromptContextValue(context.subaction?.actionType)}
Subaction duration: ${formatPromptContextValue(context.subaction?.duration)}
Subaction intensity level: ${formatPromptContextValue(context.subaction?.intensityLevel)}
Subaction notes: ${formatPromptContextValue(context.subaction?.notes)}
Subaction primary intent: ${formatPromptContextValue(context.subaction?.primaryIntent)}
Subaction secondary benefits: ${formatPromptContextValue(context.subaction?.secondaryBenefits)}
Subaction body regions: ${formatPromptContextValue(context.subaction?.bodyRegions)}
Subaction movement qualities: ${formatPromptContextValue(context.subaction?.movementQualities)}
Subaction user use cases: ${formatPromptContextValue(context.subaction?.userUseCases)}
Subaction tags: ${formatPromptContextValue(context.subaction?.tags)}`;

export type PromptStarters = {
  introMessages: string[];
  suggestedReplies: string[];
};

/**
 * Generate the opening of a prompt subaction's chat: a couple of authored intro
 * bubbles (assistant voice) plus a few tappable starter replies (user voice).
 * Used by the "Generate starters" button on prompt subactions.
 */
export async function generatePromptStarters(
  context: ReflectionPromptPackContext
): Promise<PromptStarters> {
  const instruction = `
You write the opening of an AI assistant chat inside a community app.

Using the context below, produce:
- introMessages: 1 to 2 short opening chat bubbles, written AS the assistant speaking directly to the user, in the author's voice when an author is available. Warm and natural, no self-introduction, no bio, no announcing the topic. Each bubble is one or two short sentences. Open the conversation like picking up with someone you already know.
- suggestedReplies: 3 to 4 short first replies the USER might tap to begin, written in the user's first-person voice. Each under about 8 words. Make them distinct and specific to the context.

Rules:
- Return ONLY valid JSON in this exact shape: {"introMessages": ["..."], "suggestedReplies": ["..."]}.
- Plain conversational language. No placeholders, brackets, angle brackets, or template labels.
- Do not name or quote the exact community, habit, action, or subaction labels.
- Do not assume the user has already tried, completed, or documented anything.
- Write everything in the language of the context below.

Context:
${buildPromptPackContextBlock(context)}
`;

  const text = await callGemini([{ text: instruction }], 700, false, "application/json");
  const parsed = parseJSON<Partial<PromptStarters>>(text);

  return {
    introMessages: sanitizeStringArray(parsed?.introMessages).slice(0, 4),
    suggestedReplies: sanitizeStringArray(parsed?.suggestedReplies).slice(0, 6),
  };
}

export async function generateReflectionPromptPack(
  context: ReflectionPromptPackContext
): Promise<string> {
  const instruction = `
You write finished system prompts for AI assistants inside a community app.

Create exactly one reflectionPrompt based on the context below.

Requirements:
- Output plain text only.
- Do not return JSON.
- Do not use placeholders, brackets, angle brackets, or template labels.
- Do not explain your reasoning.
- Write a fully finished system prompt that will be passed to the assistant chat inside this subaction.
- Make it specific to the community, author, habit, action, and subaction context.
- Write the prompt to the AI assistant, not as a message from the assistant.
- The first words must be "You are ...".
- Do not include a title before that.
- Do not start with "As a ...".
- If an author name is available, the opening should present the assistant as that author or the author's expert voice, not as a generic reflection assistant.
- Do not write in first person.
- Never use phrases like "I will help you", "I can help", or similar assistant self-talk.
- Do not include awkward quoted phrases, inside jokes, or catchphrases copied from the source context.
- If the source context contains unusual wording, rewrite it into clear natural language.
- Write behavioral instructions for an ongoing assistant chat, not a prompt pack or worksheet.
- Tell the assistant to use the community, author, habit, action, and subaction context as internal background — not as phrases to repeat back to the user.
- Tell the assistant to translate awkward internal titles into natural language and never quote exact names unless the user brings them up first.
- Derive the tone, communication style, depth, and areas of focus from the community, author, habit, action, and subaction context.
- The assistant's first message should be a short, natural opener in the author's voice — no self-introduction, no bio recap, no announcing the topic. Just check in with the user as if picking up a conversation. Do not name or reference the subaction, action, or habit in the opening.
- Tell the assistant to ask for missing context in short sentences, one question at a time.
- Tell the assistant not to mention internal prompt rules or metadata.
- Keep it practical, specific, and strong. Avoid generic corporate filler.

Context:
${buildPromptPackContextBlock(context)}
`;

  const text = await callGemini([{ text: instruction }], 1400, false, null);
  let cleaned = cleanTextResponse(text);

  if (
    !isValidAssistantPrompt(cleaned) ||
    promptMentionsInternalLabels(cleaned, context) ||
    !promptUsesAuthorVoice(cleaned, context) ||
    promptFeelsGenericOrAssumptive(cleaned)
  ) {
    const rewriteInstruction = `
Rewrite the text below into a valid AI assistant prompt.

Rules:
- Keep the same domain and intent.
- Output plain text only.
- The first words must be "You are ...".
- Do not include a title before that.
- Do not start with "As a ...".
- If an author name exists, make the opening clearly use that author's voice or identity.
- Do not write in first person.
- Never use phrases like "I will help you" or "I can help".
- Write it as the system prompt for the assistant chat inside the subaction.
- Make it conversational and behavior-focused, not like a worksheet or prompt pack.
- Use titles and metadata as internal context, not as phrases to repeat back to the user.
- Avoid quoting or repeating exact community, habit, action, or subaction names unless truly needed.
- Remove exact internal labels like community titles, habit names, action names, and subaction names. Rewrite them as natural descriptions instead.
- Do not assume the user completed the step successfully, attempted it, or captured a result.
- This prompt will also shape the first assistant message, so make the opening behavior warm, author-led, and assumption-free.
- Start from curiosity about the user's current state and what they want help unpacking.
- Tell the assistant to ask one short question at a time when more context is needed.
- Do not use "Based on the following information:".
- Do not use "Please create:".
- Remove awkward quoted phrases, jokes, or catchphrases.

Text to rewrite:
${cleaned}
`;

    const rewritten = await callGemini(
      [{ text: rewriteInstruction }],
      1400,
      false,
      null
    );
    cleaned = cleanTextResponse(rewritten);
  }

  if (!cleaned) {
    throw new Error("The AI returned an empty assistant prompt.");
  }

  return cleaned;
}

export type VoiceCloneResult = {
  voiceId: string;
  requiresVerification: boolean;
};

export type AgentCreateResult = {
  agentId: string;
};

type AgentPromptDraft = {
  agentName: string;
  firstMessage: string;
  systemPrompt: string;
};

export type DefaultAgentVoice = {
  /** ElevenLabs premade voice_id, stable across every account */
  id: string;
  /** Human label, used in logs and any future picker UI */
  label: string;
  gender: "female" | "male";
  /** Tone this voice fits best */
  persona: "warm" | "calm" | "energetic" | "authoritative";
};

// Curated ElevenLabs premade voices. They work with the multilingual model, so
// they speak the community language regardless of the accent label. Used as a
// sensible default when the creator does not clone their own voice during
// onboarding, so the community still launches with a working voice agent.
export const DEFAULT_AGENT_VOICES: DefaultAgentVoice[] = [
  { id: "EXAVITQu4vr4xnSDxMaL", label: "Sarah", gender: "female", persona: "warm" },
  { id: "XrExE9yKIg1WjnnlVkGX", label: "Matilda", gender: "female", persona: "warm" },
  { id: "LcfcDJNUP1GQjkzn1xUU", label: "Emily", gender: "female", persona: "calm" },
  { id: "pFZP5JQG7iQjIQuC4Bku", label: "Lily", gender: "female", persona: "energetic" },
  { id: "JBFqnCBsd6RMkjVDRZzb", label: "George", gender: "male", persona: "warm" },
  { id: "onwK4e9ZLuTAKqWW03F9", label: "Daniel", gender: "male", persona: "authoritative" },
  { id: "nPczCjzI2devNBz1zQrb", label: "Brian", gender: "male", persona: "calm" },
  { id: "TxGEqnHWrfWFTfGW9XjX", label: "Josh", gender: "male", persona: "energetic" },
];

// Safe fallback when nothing maps cleanly — a balanced, warm voice.
export const FALLBACK_AGENT_VOICE_ID = "EXAVITQu4vr4xnSDxMaL";

const CALM_VOICE_HINTS = [
  "meditat",
  "mindful",
  "sleep",
  "calm",
  "spiritual",
  "yoga",
  "breath",
  "relax",
  "rest",
  "heal",
];
const ENERGETIC_VOICE_HINTS = [
  "fitness",
  "workout",
  "exercise",
  "hiit",
  "energy",
  "sport",
  "train",
  "motivat",
  "hustle",
  "performance",
];
const AUTHORITATIVE_VOICE_HINTS = [
  "business",
  "finance",
  "invest",
  "money",
  "strategy",
  "leadership",
  "career",
  "sales",
  "entrepreneur",
  "wealth",
];

const matchesVoiceHint = (text: string, hints: string[]): boolean =>
  hints.some((hint) => text.includes(hint));

/**
 * Map a community's context to one of the curated default voices.
 * Used when no creator voice was cloned during onboarding, so the agent still
 * launches with a fitting voice instead of being skipped.
 */
export function pickDefaultAgentVoice({
  categoryName,
  writingStyle,
  tags,
  preferredGender,
}: {
  categoryName?: string;
  writingStyle?: string;
  tags?: string[];
  preferredGender?: "female" | "male";
}): DefaultAgentVoice {
  const haystack = [categoryName, writingStyle, ...(tags || [])]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const persona: DefaultAgentVoice["persona"] = matchesVoiceHint(haystack, CALM_VOICE_HINTS)
    ? "calm"
    : matchesVoiceHint(haystack, ENERGETIC_VOICE_HINTS)
      ? "energetic"
      : matchesVoiceHint(haystack, AUTHORITATIVE_VOICE_HINTS)
        ? "authoritative"
        : "warm";

  const byPersona = DEFAULT_AGENT_VOICES.filter((voice) => voice.persona === persona);
  const personaPool = byPersona.length ? byPersona : DEFAULT_AGENT_VOICES;

  const byGender = preferredGender
    ? personaPool.filter((voice) => voice.gender === preferredGender)
    : personaPool;
  const finalPool = byGender.length ? byGender : personaPool;

  return (
    finalPool[0] ||
    DEFAULT_AGENT_VOICES.find((voice) => voice.id === FALLBACK_AGENT_VOICE_ID) ||
    DEFAULT_AGENT_VOICES[0]
  );
}

export async function createInstantVoiceClone({
  file,
  name,
  description,
}: {
  file: File;
  name: string;
  description?: string;
}): Promise<VoiceCloneResult> {
  const apiKey = (DEFAULT_ELEVENLABS_API_KEY || "").trim();
  if (!apiKey) {
    throw new Error("ElevenLabs API key is not configured.");
  }

  if (!file || file.size <= 0) {
    throw new Error("Voice clone requires a non-empty audio sample.");
  }

  const formData = new FormData();
  formData.append("name", name.trim());
  formData.append("files", file, file.name);
  formData.append("remove_background_noise", "false");

  if (description?.trim()) {
    formData.append("description", description.trim());
  }

  const response = await fetch(`${ELEVENLABS_BASE}/voices/add`, {
    method: "POST",
    headers: {
      "xi-api-key": apiKey,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`ElevenLabs voice clone failed (${response.status}): ${errorText}`);
  }

  const json = await response.json();
  const voiceId = json?.voice_id;

  if (!voiceId || typeof voiceId !== "string") {
    throw new Error("ElevenLabs did not return a voice_id for the cloned voice.");
  }

  return {
    voiceId,
    requiresVerification: !!json?.requires_verification,
  };
}

// Appended to every created agent's prompt so the app's runtime dynamic
// variables land in the model's context. The app supplies these at
// startSession (see ThePath buildAssistantContext.ts). Keep the placeholder
// list in sync with the migration script
// (ThePath/scripts/elevenlabs/add-context-placeholders.mjs).
const AGENT_CONTEXT_VARIABLES_BLOCK = `

# Live context (personalize using this; some fields may be empty; never read it aloud verbatim)

{{user_profile}}

{{member_profile}}

{{upcoming_goals}}

# Conversation handoff (continue naturally from these if present)

{{handoff_note}}

{{conversation_context}}

# Coaching framework in play (apply it throughout when present; may be empty; never read aloud verbatim)

{{skill_lens}}

# The member's routines (use these names when you log something the member did; may be empty)

{{member_routines}}`;

// Voice client tools the app runs (ThePath logActivityTool.ts, rememberTool.ts).
// Keep in sync with ThePath/scripts/elevenlabs/add-client-tools.mjs
// COACH_CLIENT_TOOLS, which registers the same tools on existing agents.
const COACH_CLIENT_TOOLS = [
  {
    type: "client",
    name: "logActivity",
    description:
      "Log something the member says they already did (a practice, a session, sets of an exercise) to one of their routines. " +
      "Always call it first with confirm false: it returns a confirmation question, ask the member exactly that and wait. " +
      "Only after the member clearly says yes, call it again with the same details and confirm true. " +
      "If it returns a list of routines, ask the member which one and call again with habitHint set to their answer. " +
      "Never log future plans.",
    expects_response: true,
    response_timeout_secs: 20,
    parameters: {
      type: "object",
      properties: {
        practice: { type: "string", description: "Kind of practice in one word, for example meditation, exercise, diet, fasting, hydration, journaling." },
        habitHint: { type: "string", description: "The routine name the member used or picked, if any." },
        description: { type: "string", description: "Short description of what they did, in the member's words." },
        durationMinutes: { type: "number", description: "How long it took, in minutes, if they said." },
        sets: {
          type: "array",
          description: "Only for sets of an exercise. One item per set, or use setCount for identical sets.",
          items: {
            type: "object",
            properties: {
              exercise: { type: "string", description: "Exercise name." },
              reps: { type: "number", description: "Repetitions in the set." },
              weight: { type: "number", description: "Load used, as a number." },
              unit: { type: "string", description: "Unit of the load, kg or lb." },
              setCount: { type: "number", description: "How many identical sets this item stands for. Default 1." },
            },
          },
        },
        note: { type: "string", description: "Anything else worth keeping with the log, such as how it felt." },
        date: { type: "string", description: "Day it happened: today, yesterday, or YYYY-MM-DD. Leave empty for today. Never a future day." },
        confirm: { type: "boolean", description: "false to get the confirmation question. true only after the member said yes to that question." },
      },
      required: ["confirm"],
    },
  },
  {
    type: "client",
    name: "rememberAboutUser",
    description:
      "Save a durable fact about the member that will help future sessions. Only call it for a recurring rule, preference, observed pattern or fixed constraint, never for one-off feelings or today-only events. Do not announce that you saved it.",
    expects_response: true,
    response_timeout_secs: 20,
    parameters: {
      type: "object",
      properties: {
        category: {
          type: "string",
          enum: ["routines", "preferences", "patterns", "constraints"],
          description: "routines: recurring rules. preferences: how they like to work. patterns: states seen across sessions. constraints: hard limits or fixed life facts.",
        },
        fact: { type: "string", description: "One short third-person sentence that stays true across sessions, with no dates and no 'today'." },
      },
      required: ["category", "fact"],
    },
  },
];

// Finds or creates the workspace tools above and returns their ids. Never
// throws, and one tool failing never drops the other: an agent without a tool
// still works, it just cannot run that one.
async function ensureCoachClientToolIds(apiKey: string): Promise<string[]> {
  const headers = { "Content-Type": "application/json", "xi-api-key": apiKey };
  const byName = new Map<string, string>();
  try {
    let cursor: string | null = null;
    do {
      const qs: string = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
      const listRes = await fetch(`${ELEVENLABS_BASE}/convai/tools${qs}`, { headers });
      if (!listRes.ok) throw new Error(`list tools failed (${listRes.status})`);
      const listJson = await listRes.json();
      for (const tool of listJson?.tools || []) {
        const name = tool?.tool_config?.name;
        // Same-name duplicates: the first one wins, never attach two.
        if (name && tool?.id && !byName.has(name)) byName.set(name, tool.id);
      }
      cursor = listJson?.has_more && listJson?.next_cursor ? listJson.next_cursor : null;
    } while (cursor);
  } catch (err) {
    // Without the list a create could duplicate a tool, so attach none.
    console.warn("[createCommunityAgent] client tools not attached:", err);
    return [];
  }
  const ids: string[] = [];
  for (const config of COACH_CLIENT_TOOLS) {
    try {
      let id = byName.get(config.name);
      if (!id) {
        const createRes = await fetch(`${ELEVENLABS_BASE}/convai/tools`, {
          method: "POST",
          headers,
          body: JSON.stringify({ tool_config: config }),
        });
        if (!createRes.ok) throw new Error(`create failed (${createRes.status})`);
        id = (await createRes.json())?.id;
      }
      if (id) ids.push(id);
    } catch (err) {
      console.warn(`[createCommunityAgent] client tool ${config.name} not attached:`, err);
    }
  }
  return ids;
}

export async function createCommunityAgent({
  communityName,
  communityDescription,
  categoryName,
  tags,
  writingStyle,
  authorName,
  authorDescription,
  communityAiGuidelines,
  authorAiGuidelines,
  voiceId,
  language,
}: {
  communityName: string;
  communityDescription?: string;
  categoryName?: string;
  tags?: string[];
  writingStyle?: string;
  authorName?: string;
  authorDescription?: string;
  communityAiGuidelines?: string;
  authorAiGuidelines?: string;
  voiceId: string;
  /** BCP 47 community language code (e.g. "en", "es"); defaults to "en" */
  language?: string;
}): Promise<AgentCreateResult> {
  const apiKey = (DEFAULT_ELEVENLABS_API_KEY || "").trim();
  if (!apiKey) {
    throw new Error("ElevenLabs API key is not configured.");
  }

  const promptDraft = await generateCommunityAgentPrompt({
    communityName,
    communityDescription,
    categoryName,
    tags,
    writingStyle,
    authorName,
    authorDescription,
    communityAiGuidelines,
    authorAiGuidelines,
  });

  const systemPromptWithContext = `${promptDraft.systemPrompt}${AGENT_CONTEXT_VARIABLES_BLOCK}`;
  const toolIds = await ensureCoachClientToolIds(apiKey);

  // ElevenLabs expects a primary language subtag ("es"), not a full BCP 47 tag ("es-MX")
  const agentLanguage =
    language?.trim().toLowerCase().split("-")[0] || "en";

  const body = {
    name: promptDraft.agentName,
    tags: [
      "community",
      "onboarding",
      (communityName.trim() || "community").toLowerCase(),
      ...(categoryName?.trim() ? [categoryName.trim().toLowerCase()] : []),
    ],
    conversation_config: {
      agent: {
        language: agentLanguage,
        first_message: promptDraft.firstMessage,
        prompt: {
          prompt: systemPromptWithContext,
          llm: ELEVENLABS_AGENT_LLM,
          ...(toolIds.length ? { tool_ids: toolIds } : {}),
        },
      },
      tts: {
        voice_id: voiceId.trim(),
        // v3 conversational is English-only for agents; other languages must
        // use turbo v2_5 or ElevenLabs rejects the create with a 400.
        model_id:
          agentLanguage === "en"
            ? ELEVENLABS_AGENT_TTS_MODEL
            : ELEVENLABS_AGENT_TTS_MODEL_NON_EN,
      },
    },
  };

  const response = await fetch(`${ELEVENLABS_BASE}/convai/agents/create`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "xi-api-key": apiKey,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`ElevenLabs agent creation failed (${response.status}): ${errorText}`);
  }

  const json = await response.json();
  const agentId = json?.agent_id;

  if (!agentId || typeof agentId !== "string") {
    throw new Error("ElevenLabs did not return an agent_id for the created agent.");
  }

  return { agentId };
}

const AGENT_PROMPT_REFERENCE = `# Core Function
Your primary function is to help the user define and commit to a personalized, science-backed goal that fits their real life.
Education and coaching only serve the purpose of identifying the right next step for this specific person, in their current context, and helping them follow through.
You do not rush to recommendations until personalization is sufficient.
# Identity
You speak as the creator and expert voice of the community.
You are optimistic, direct, empathetic, and practical.
# Environment
You deliver voice-based coaching sessions inside the community app.
Users come for practical guidance, accountability, and clear next steps.
# Interaction Strategy (Critical)
1. Identify the dominant constraint.
2. Calibrate context through a natural exchange.
3. Explain the relevant principle in simple terms.
4. Propose one practical next-step protocol.
5. Confirm readiness before moving forward.
# Tone & Voice
Use short spoken sentences.
Reference the user's context naturally.
Stay focused on practical change.
# Goal Framing Rules
Recommend one clear next step at a time.
Make it specific, observable, and realistic.
# Guardrails
Do not diagnose.
Do not over-question.
Do not mention being an AI.
# Text-to-Speech Rules
Use ellipses for audible pauses.
Spell out acronyms when needed.`;

function buildFallbackAgentPrompt({
  communityName,
  communityDescription,
  categoryName,
  tags,
  writingStyle,
  authorName,
  authorDescription,
  communityAiGuidelines,
  authorAiGuidelines,
}: {
  communityName: string;
  communityDescription?: string;
  categoryName?: string;
  tags?: string[];
  writingStyle?: string;
  authorName?: string;
  authorDescription?: string;
  communityAiGuidelines?: string;
  authorAiGuidelines?: string;
}): AgentPromptDraft {
  const trimmedCommunityName = communityName.trim() || "Community";
  const trimmedAuthorName = authorName?.trim() || trimmedCommunityName;
  const trimmedTags = (tags || []).filter(Boolean).join(", ");
  const sections = [
    "# Core Function",
    `Your primary function is to guide members inside "${trimmedCommunityName}" toward one clear, practical next step that fits their current situation.`,
    "Education and coaching serve one purpose only: helping the user understand the real blocker, choose the right next move, and commit to something realistic.",
    "You do not rush to recommendations until the user's context is clear enough.",
    "# Identity",
    `You are ${trimmedAuthorName}, the expert voice of "${trimmedCommunityName}".`,
    authorDescription?.trim() || "You speak with conviction, empathy, and practical clarity.",
    "# Environment",
    `You deliver voice-based coaching inside the ${trimmedCommunityName} app.`,
    categoryName?.trim() ? `Community category: ${categoryName.trim()}.` : "",
    communityDescription?.trim() ? `Community focus: ${communityDescription.trim()}` : "",
    trimmedTags ? `Relevant topics: ${trimmedTags}.` : "",
    "# Interaction Strategy (Critical)",
    "1. Identify the user's dominant constraint or desired outcome.",
    "2. Calibrate context with a brief natural exchange.",
    "3. Explain the relevant principle in plain language.",
    "4. Propose one practical next step that fits the user's real life.",
    "5. Confirm readiness before closing the conversation.",
    "# Tone & Voice",
    writingStyle?.trim() ? writingStyle.trim() : "Use short spoken sentences. Be warm, direct, and encouraging without sounding pushy.",
    "# Community Guidance",
    communityAiGuidelines?.trim() || "Tailor recommendations to the user's context and keep advice concrete.",
    authorAiGuidelines?.trim() || "Represent the creator faithfully and avoid invented claims.",
    "# Guardrails",
    "Do not diagnose, prescribe, or invent facts.",
    "Do not mention being an AI.",
    "Do not overwhelm the user with multiple plans at once.",
    "# Text-to-Speech Rules",
    "Use ellipses (...) for audible pauses.",
    "Use natural voice-friendly phrasing and avoid dense paragraphs.",
  ].filter(Boolean);

  return {
    agentName: `${trimmedCommunityName} Agent`,
    firstMessage: `Hi, this is ${trimmedAuthorName}. What would you like help with today?`,
    systemPrompt: sections.join("\n"),
  };
}

async function generateCommunityAgentPrompt({
  communityName,
  communityDescription,
  categoryName,
  tags,
  writingStyle,
  authorName,
  authorDescription,
  communityAiGuidelines,
  authorAiGuidelines,
}: {
  communityName: string;
  communityDescription?: string;
  categoryName?: string;
  tags?: string[];
  writingStyle?: string;
  authorName?: string;
  authorDescription?: string;
  communityAiGuidelines?: string;
  authorAiGuidelines?: string;
}): Promise<AgentPromptDraft> {
  const fallback = buildFallbackAgentPrompt({
    communityName,
    communityDescription,
    categoryName,
    tags,
    writingStyle,
    authorName,
    authorDescription,
    communityAiGuidelines,
    authorAiGuidelines,
  });

  const promptContext = {
    communityName,
    communityDescription: communityDescription || "",
    categoryName: categoryName || "",
    tags: tags || [],
    writingStyle: writingStyle || "",
    authorName: authorName || "",
    authorDescription: authorDescription || "",
    communityAiGuidelines: communityAiGuidelines || "",
    authorAiGuidelines: authorAiGuidelines || "",
  };

  try {
    const json = await createOpenAIChatCompletion<any>({
      model: OPENAI_AGENT_MODEL,
      messages: [
        {
          role: "system",
          content:
            "You write production-ready system prompts for voice-first ElevenLabs community agents. Return only valid JSON with keys agentName, firstMessage, and systemPrompt.",
        },
        {
          role: "user",
          content: `Use this reference prompt as the structural style guide, not as content to copy literally:\n\n${AGENT_PROMPT_REFERENCE}\n\nCreate a customized ElevenLabs agent prompt for this community:\n${JSON.stringify(
            promptContext,
            null,
            2
          )}\n\nRequirements:\n- Keep the same general sectioned style as the reference prompt.\n- Adapt the content to the actual community, creator, and audience.\n- Write for a voice-based assistant inside the app.\n- Make the agent useful for welcoming users, answering questions, clarifying the community method, and guiding the user toward one practical next step.\n- Do not invent tools or tool contracts unless explicit tool information is provided.\n- Do not mention diagnoses, prescriptions, or unsupported claims.\n- Keep the first message under 18 words.\n- Keep the system prompt concise but specific.\n- Return only valid JSON with this exact shape:\n{\n  "agentName": "string",\n  "firstMessage": "string",\n  "systemPrompt": "string"\n}`,
        },
      ],
    });
    const rawContent = json?.choices?.[0]?.message?.content || "";
    const parsed = parseJSON<Partial<AgentPromptDraft>>(rawContent);

    if (
      typeof parsed?.agentName !== "string" ||
      typeof parsed?.firstMessage !== "string" ||
      typeof parsed?.systemPrompt !== "string"
    ) {
      return fallback;
    }

    return {
      agentName: parsed.agentName.trim() || fallback.agentName,
      firstMessage: parsed.firstMessage.trim() || fallback.firstMessage,
      systemPrompt: parsed.systemPrompt.trim() || fallback.systemPrompt,
    };
  } catch (error) {
    console.warn("OpenAI agent prompt generation error:", error);
    return fallback;
  }
}

// ─── Build content parts from sources ────────────────────────────────────────

export async function buildParts(
  sources: ContentSources,
  instructionText: string
): Promise<any[]> {
  const parts: any[] = [];

  // Convert binary files (PDFs, DOCX) to inline base64 for Gemini
  if (sources.uploadedFiles?.length) {
    for (const f of sources.uploadedFiles) {
      if (f.file && f.mimeType !== "text/plain") {
        try {
          const inlinePart = await fileToInlinePart(f.file);
          parts.push(inlinePart);
        } catch (e) {
          console.warn(`Could not process ${f.name}:`, e);
        }
      } else if (f.text) {
        parts.push({ text: `[${f.name}]:\n${f.text}` });
      }
    }
  }

  // Text-based sources
  const textParts: string[] = [];
  if (sources.websiteUrl) textParts.push(`Website URL: ${sources.websiteUrl}`);

  if (sources.pastedContent) textParts.push(`Creator content:\n${sources.pastedContent}`);

  if (textParts.length) parts.push({ text: textParts.join("\n\n") });

  if (parts.length === 0) {
    parts.push({
      text: "No content provided — generate reasonable defaults for a general online community.",
    });
  }

  // Append the instruction
  parts.push({ text: instructionText });

  return parts;
}

// ─── Generation functions ─────────────────────────────────────────────────────

// Shared JSON rules appended to every prompt
export const JSON_RULES = `
RULES:
- Return ONLY valid JSON. No markdown, no backticks, no extra text outside the JSON.
- All string fields must be concise and filled.
- JSON must be parseable with JSON.parse().`;

// Language rule appended to every content-generation prompt
export const LANGUAGE_RULE = `
LANGUAGE: Detect the language of the source content above. Generate ALL text fields in that same language. If the source is in Spanish, write everything in Spanish. If in French, write in French. Do not translate to English unless the source is in English.`;

// Shared tail combined with each community's aiPrompt at call time.
// Defines the output schema, field writing instructions, and enforcement rules.
// Store only the community-specific header in the DB; combine with this at runtime.
export const AI_PROMPT_SHARED_TAIL = `
---

OUTPUT
Return ONLY the following strict JSON schema (all fields required, no comments, no backticks, no extra keys):

{
  "ai_result": {
    "heading": "string",
    "subheading": "string",
    "archetype": {
      "name": "string",
      "tagline": "string",
      "symbol": "string"
    },
    "insights": ["string"],
    "metrics": [
      { "label": "string", "current": 0, "potential": 0 }
    ],
    "diagnosis": {
      "title": "string",
      "summary": "string",
      "confidence": 0.00
    },
    "cost": {
      "who": "string",
      "amount": "string",
      "period": "string",
      "other_costs": ["string"]
    },
    "reframe": "string",
    "plan_preview": {
      "title": "string",
      "bullets": ["string"]
    },
    "cta": { "label": "Unlock My Plan" }
  }
}

---

HOW TO WRITE

heading
One-line hook revealing the real blocker (offer, audience, validation, consistency, traffic, or execution).
Example: "Your problem isn't your product — it's lack of a simple offer that converts."

subheading
Shift blame from the creator to the system or process.
Example: "You don't need more motivation — you need a structure that removes guesswork."

archetype
A short identity label the user can claim, derived from USER_ANSWERS. This is the hero moment of the screen, so it must feel uniquely theirs, not generic.
- name: 2–4 words starting with "The". Specific, evocative, never insulting. Examples: "The Restless Builder", "The Burned-Out Visionary", "The Quiet Climber", "The Stalled Strategist".
- tagline: ONE sentence (max ~110 chars) that defines the archetype in human terms and hints at what's holding them back.
- symbol: a single uppercase letter (1 character) to render inside the hero badge. Prefer the first letter of the core noun in the name (e.g. "B" for "The Restless Builder").

insights
3 short realizations that feel accurate and relatable based on USER_ANSWERS.

metrics
3–5 performance metrics (integers 0–100). Estimate current based on USER_ANSWERS, potential based on what's achievable with the plan.
Choose from: Offer Clarity, Audience Validation, Lead Flow, Content Consistency, Conversion Readiness, Launch Momentum, Revenue Predictability, Execution Focus, Time Leverage, System Simplicity.

diagnosis
Short title + 1–2 sentence explanation of the hidden bottleneck using USER_ANSWERS.

cost
Quantify the impact: delayed revenue, lost momentum, or wasted time.
Examples: "$1,000–$5,000 in delayed monthly revenue", "3–6 months of stalled progress".

reframe
Position the community plan as the fastest, simplest path to traction and momentum.

plan_preview
3–5 tangible actions the plan will cover.

cta
Label must read exactly: "Unlock My Plan"

---

RULES

* All fields must be filled and concise.
* Confidence must be between 0.00 and 1.00 (two decimals).
* Metrics values must be integers between 0 and 100.
* Keep total content under 200 words.
* Personalize using USER_ANSWERS.
* JSON must be valid and parseable.
* No extra text outside the JSON.

NOW PRODUCE ONLY THE JSON WITH THE "ai_result" OBJECT.`;

async function generateCommunityAndAuthor(
  sources: ContentSources
): Promise<{ community: GeneratedDraft; author: AuthorDraft }> {
  const categoriesList = COMMUNITY_CATEGORIES.join(", ");

  const instruction = `
You are setting up a community platform for a creator. Analyze the content above and extract everything needed.

Available categories: ${categoriesList}

Return ONLY valid JSON with this exact structure:
{
  "community": {
    "title": "Community name extracted or inferred from their content",
    "description": "2-3 compelling sentences specific to their audience",
    "shortDescription": "One hook sentence under 15 words",
    "categoryName": "One of the available categories",
    "tags": ["5-7 relevant tags"],
    "writingStyle": "2-3 sentences on tone and vocabulary detected in their content",
    "visualStyle": "2-3 sentences on visual aesthetic that fits their brand",
    "aiGuidelines": "3-4 sentences: voice rules, what to avoid, how to frame content for this audience",
    "primaryColor": "#hexcode — dominant brand color inferred from their content, logo, or niche. Just the #hexcode, no comments.",
    "secondaryColor": "#hexcode — complementary accent color that pairs well with the primary. Just the #hexcode, no comments.",
    "lang": "BCP 47 language code inferred from the content language (e.g. 'en', 'es', 'fr', 'pt', 'de')",
    "stylePack": "Visual preset for the app UI. Exactly one of: energetic (sharp corners, strong shadows: fitness, training, high-intensity coaching), zen (soft large radii, flat warm surfaces: meditation, spirituality, healing), editorial (small radii, flat, stark neutrals: business, learning, career), calmPower (warm ivory surfaces, quiet contrast: stress, burnout, nervous-system and high-end lifestyle coaching), classic (rounded, soft shadows: general purpose default). Pick by the creator's niche and tone."
  },
  "author": {
    "fullName": "Creator's full name extracted from content",
    "description": "2-3 sentence bio written in 3rd person",
    "shortDescription": "One punchy sentence under 12 words describing who they are",
    "aiGuidelines": "2-3 sentences: how AI should represent this author's voice and expertise",
    "tags": ["3-5 tags describing their expertise"]
  }
}
${LANGUAGE_RULE}
${JSON_RULES}`;

  const hasUrl = !!sources.websiteUrl;
  const parts = await buildParts(sources, instruction);
  const text = await callGemini(parts, 4000, hasUrl);
  return parseJSON(text);
}

async function generateHabitsAndQuotes(
  sources: ContentSources
): Promise<{ habits: HabitDraft[]; quotes: QuoteDraft[] }> {
  const instruction = `
You are building content for an online community. Analyze the creator's content above.

actionType must be one of: "exercise", "diet", "meditation", "sleep", "custom".
practiceType is an integer classifying the WHOLE habit. Pick the best fit and keep it consistent with the actions: 1=meditation/mindfulness, 2=diet/nutrition, 3=exercise/movement, 5=fasting, 6=custom, 7=hydration, 8=focus/deep work, 11=regulation toolkit, 4=default (use only when none of the others fit). Do not default everything to 4.
For each habit, generate 2-3 actions. Each action should have 2-3 subactions.
Use "reflectionPrompt" on subactions where personalized AI guidance makes sense (e.g. reflection, journaling, coaching check-ins, planning exercises). The reflectionPrompt is not a user-facing question. It is the system prompt for the assistant chat inside that subaction. Generate the actual prompt, not placeholders, bracket labels, titles, or meta instructions. Start with "You are ...". If an author exists, make "You are ..." clearly use the author's voice or identity rather than a generic assistant role. Make it a concise system prompt that tells the assistant how to guide the subaction chat using the Community, Author, Habit, Action, and Subaction context. The prompt should shape the first message too, so it must not assume the user already tried or completed anything. It should tell the assistant to open warmly, ask for missing information in short sentences one at a time, help the user uncover wins, blockers, patterns, and a practical next step, and avoid repeating exact internal action or habit names unless the user brings them up first.

Return ONLY valid JSON:
{
  "habits": [
    {
      "habitName": "Habit name",
      "habitDescription": "2-3 sentences on what it involves and why it matters",
      "targetFrequency": "Daily | 3x per week | Weekly",
      "practiceType": 3,
      "actions": [
        {
          "name": "Action name",
          "description": "What this action involves, 1-2 sentences",
          "actionType": "custom",
          "duration": 15,
          "goal": "What the user achieves by completing this action",
          "subactions": [
            {
              "name": "Subaction name",
              "description": "What the user does, 1-2 sentences",
              "actionType": "custom",
              "duration": 5,
              "intensityLevel": "low",
              "reflectionPrompt": "You are Herb Lipsman, the experienced and practical voice guiding this subaction chat. Help the user think clearly about what they want to understand, what feels unclear, and what would be most useful to unpack right now. Use the broader coaching context internally, but avoid repeating internal program labels unless the user brings them up first. Keep responses concise, warm, and practical. The first message should open naturally without assuming the user already completed, attempted, or documented anything. When more context is needed, ask one short question at a time. Reflect back meaningful details before offering guidance, and focus on insight, friction, and the next best step rather than sounding like a checklist or task review."
            }
          ]
        }
      ]
    }
  ],
  "quotes": [
    {
      "quote": "Motivational quote aligned with this creator's voice and niche",
      "authorName": "Author name"
    }
  ]
}

Generate exactly 5 habits and 8 quotes. Everything must feel authentic to this creator's brand.
${LANGUAGE_RULE}
${JSON_RULES}`;

  const hasUrl = !!sources.websiteUrl;
  const parts = await buildParts(sources, instruction);
  const text = await callGemini(parts, 16000, hasUrl);
  const parsed = parseJSON<{ habits?: any[]; quotes?: any[] }>(text);
  // Normalize habits (clamps actionType/intensity, validates practiceType and
  // falls back to inference) instead of trusting the raw model output.
  const habits = Array.isArray(parsed?.habits)
    ? parsed.habits.map((habit) => normalizeHabitDraft(habit))
    : [];
  const quotes = Array.isArray(parsed?.quotes)
    ? (parsed.quotes as QuoteDraft[])
    : [];
  return { habits, quotes };
}

async function generateQuestionnaire(
  sources: ContentSources
): Promise<QuestionnaireDraft> {
  const instruction = `
You are designing an onboarding questionnaire for a new community member. Analyze the creator's content above to understand their niche, audience pains, and promised outcomes.

Generate 6-8 onboarding questions that uncover the member's pains, goals, current situation, and expected outcome from the program. Question types: SINGLE_SELECTION, MULTIPLE_SELECTION, TEXT.

Also generate:
- beforeAfter: concise "before" frustrations and "after" outcomes this community helps create
- commit: the commitment-screen greeting, subcopy, and 3-5 short commitments
- reviews: 3 representative customer/member reviews aligned with the creator's likely outcomes and audience transformation

For reviews, write representative testimonial-style social proof. Do not invent company names, exact revenue numbers, or highly specific claims unless they are clearly supported by the source content.

Then write a partial aiPrompt containing ONLY the community-specific header sections listed below — do NOT include output schema, field instructions, or rules (those are added automatically).

The aiPrompt must contain exactly these sections in order:
1. PERSONA (1 line): "You are [creator's name/persona] — [role + niche description] inside the **"[community name]"** app."
2. GOAL (1 line): "Your goal is to translate the user's onboarding answers into one persuasive, practical insight that makes them feel understood, builds trust in the system, and drives them to unlock a personalized plan."
3. CONTEXT section: bullet list of topics covered in the onboarding questions you generated
4. VOICE & STYLE section: 4-5 bullets describing the creator's tone, focus areas, emoji usage (max 2 per section), word count target (under 200 words)
5. USER_ANSWERS placeholder (verbatim): "USER_ANSWERS (verbatim JSON):\\n{{USER_ANSWERS_JSON}}"

Return ONLY valid JSON:
{
  "questions": [
    {
      "id": "q1",
      "question": "Question text",
      "type": "SINGLE_SELECTION",
      "options": [{ "description": "Option label", "value": "option_value" }]
    },
    {
      "id": "q2",
      "question": "Open question",
      "type": "TEXT",
      "placeholder": "Your answer..."
    }
  ],
  "beforeAfter": {
    "before": [
      "Short frustration before joining"
    ],
    "after": [
      "Short outcome after following the program"
    ]
  },
  "commit": {
    "greeting": "Short commitment headline",
    "commitments": [
      "Short commitment promise"
    ],
    "subcopy": "1-2 sentence explanation of what committing means in this program"
  },
  "reviews": [
    {
      "name": "First name",
      "text": "Representative review from a satisfied member or customer",
      "stars": 5
    }
  ],
  "aiPrompt": "community-specific header sections only (persona, goal, context, voice, user_answers placeholder)"
}
${LANGUAGE_RULE}
${JSON_RULES}`;

  const hasUrl = !!sources.websiteUrl;
  const parts = await buildParts(sources, instruction);
  const text = await callGemini(parts, 8000, hasUrl);
  return sanitizeQuestionnaireDraft(parseJSON<Partial<QuestionnaireDraft>>(text));
}

export type QuestionnaireSuggestion = QuestionnaireDraft & {
  loadingTitle: string;
  loadingProgressLabel: string;
  loadingAlerts: string[];
  commitPrimaryCta: string;
  readyHeadline: string;
  readyBullets: string[];
  readyPrimaryCta: string;
};

export async function generateQuestionnaireSuggestion(
  sources: ContentSources
): Promise<QuestionnaireSuggestion> {
  const instruction = `
You are designing an onboarding questionnaire for a new community member. Analyze the creator's content above to understand their niche, audience pains, and promised outcomes.

Generate 6-8 onboarding questions that uncover the member's pains, goals, current situation, and expected outcome from the program. Question types: SINGLE_SELECTION, MULTIPLE_SELECTION, TEXT.

Also generate all of the following:
- beforeAfter: concise "before" frustrations and "after" outcomes this community helps create
- commit: commitment-screen greeting, subcopy, 3-5 short commitments, and a primary CTA label (e.g. "I'm in!")
- reviews: 3 representative customer/member reviews aligned with the creator's likely outcomes and audience transformation
- loading: a short loading screen title, progress label, and 3-5 rotating alert messages shown while the plan is being generated
- ready: the final "you're ready" screen — a short headline, 3-5 bullet-point outcome promises, and a primary CTA label

For reviews, write representative testimonial-style social proof. Do not invent company names, exact revenue numbers, or highly specific claims unless clearly supported by the source content.

Then write a partial aiPrompt containing ONLY the community-specific header sections listed below — do NOT include output schema, field instructions, or rules (those are added automatically).

The aiPrompt must contain exactly these sections in order:
1. PERSONA (1 line): "You are [creator's name/persona] — [role + niche description] inside the **"[community name]"** app."
2. GOAL (1 line): "Your goal is to translate the user's onboarding answers into one persuasive, practical insight that makes them feel understood, builds trust in the system, and drives them to unlock a personalized plan."
3. CONTEXT section: bullet list of topics covered in the onboarding questions you generated
4. VOICE & STYLE section: 4-5 bullets describing the creator's tone, focus areas, emoji usage (max 2 per section), word count target (under 200 words)
5. USER_ANSWERS placeholder (verbatim): "USER_ANSWERS (verbatim JSON):\\n{{USER_ANSWERS_JSON}}"

Return ONLY valid JSON:
{
  "questions": [
    {
      "id": "q1",
      "question": "Question text",
      "type": "SINGLE_SELECTION",
      "options": [{ "description": "Option label", "value": "option_value" }]
    },
    {
      "id": "q2",
      "question": "Open question",
      "type": "TEXT",
      "placeholder": "Your answer..."
    }
  ],
  "beforeAfter": {
    "before": ["Short frustration before joining"],
    "after": ["Short outcome after following the program"]
  },
  "commit": {
    "greeting": "Short commitment headline",
    "commitments": ["Short commitment promise"],
    "subcopy": "1-2 sentence explanation of what committing means in this program",
    "primaryCta": "I'm in!"
  },
  "reviews": [
    {
      "name": "First name",
      "text": "Representative review from a satisfied member or customer",
      "stars": 5
    }
  ],
  "loading": {
    "title": "Building your personalized plan...",
    "progressLabel": "Analyzing your answers",
    "alerts": ["Tip or insight shown during loading"]
  },
  "ready": {
    "headline": "Your plan is ready!",
    "bullets": ["Outcome promise bullet"],
    "primaryCta": "Start now"
  },
  "aiPrompt": "community-specific header sections only (persona, goal, context, voice, user_answers placeholder)"
}
${LANGUAGE_RULE}
${JSON_RULES}`;

  // Build a plain-text user message from the available sources
  const contentParts: string[] = [];
  if (sources.websiteUrl) contentParts.push(`Website URL: ${sources.websiteUrl}`);
  if (sources.pastedContent) contentParts.push(`Creator content:\n${sources.pastedContent}`);
  if (sources.uploadedFiles?.length) {
    for (const f of sources.uploadedFiles) {
      if (f.text) contentParts.push(`[${f.name}]:\n${f.text}`);
    }
  }
  if (contentParts.length === 0) {
    contentParts.push("No content provided — generate reasonable defaults for a general online community.");
  }
  const userMessage = contentParts.join("\n\n");

  const response = await createOpenAIChatCompletion<{ choices: { message: { content: string } }[] }>({
    model: "gpt-5-mini",
    response_format: { type: "json_object" },
    messages: [
      { role: "system", content: instruction },
      { role: "user", content: userMessage },
    ]
    });

  const text = response?.choices?.[0]?.message?.content ?? "{}";
  const raw = parseJSON<any>(text);
  const base = sanitizeQuestionnaireDraft(raw);

  return {
    ...base,
    loadingTitle: typeof raw?.loading?.title === "string" ? raw.loading.title.trim() : "",
    loadingProgressLabel: typeof raw?.loading?.progressLabel === "string" ? raw.loading.progressLabel.trim() : "",
    loadingAlerts: Array.isArray(raw?.loading?.alerts)
      ? raw.loading.alerts.map((a: any) => (typeof a === "string" ? a.trim() : "")).filter(Boolean)
      : [],
    commitPrimaryCta: typeof raw?.commit?.primaryCta === "string" ? raw.commit.primaryCta.trim() : "",
    readyHeadline: typeof raw?.ready?.headline === "string" ? raw.ready.headline.trim() : "",
    readyBullets: Array.isArray(raw?.ready?.bullets)
      ? raw.ready.bullets.map((b: any) => (typeof b === "string" ? b.trim() : "")).filter(Boolean)
      : [],
    readyPrimaryCta: typeof raw?.ready?.primaryCta === "string" ? raw.ready.primaryCta.trim() : "",
  };
}

async function generateLearningPlans(sources: ContentSources): Promise<PlanDraft[]> {
  const instruction = `
You are creating learning plans for an online community. Analyze the creator's content above thoroughly.

Generate 2-4 distinct learning plans that together cover the full breadth of the creator's material. Each plan should target a different level or topic area (e.g. Beginner / Intermediate / Advanced, or by distinct subject). Each plan should have as many days as the content warrants — do not artificially limit to 5 days. Each day should have 2-5 steps.

Learning styles available: NARRATIVE (story-driven), CAUSAL (cause & effect), SOCRATIC (question-led), MODEL (framework/model), ACTION (practical exercise).

Return ONLY valid JSON (no markdown, no extra text):
{
  "plans": [
    {
      "title": "Plan title specific to their niche",
      "shortDescription": "One compelling sentence",
      "description": "2-3 sentences on what members will achieve",
      "level": "Beginner",
      "tags": ["3-5 relevant tags"],
      "dayPlans": [
        {
          "title": "Day 1: Descriptive title",
          "description": "What this day focuses on",
          "steps": [
            {
              "title": "Step title",
              "subtitle": "Step subtitle",
              "sectionTitle": "Section name",
              "description": "1-2 sentence description of what this step covers",
              "position": 0,
              "learningStyle": "NARRATIVE"
            }
          ]
        }
      ]
    }
  ]
}
${LANGUAGE_RULE}
${JSON_RULES}`;

  const hasUrl = !!sources.websiteUrl;
  const parts = await buildParts(sources, instruction);
  const text = await callGemini(parts, 16000, hasUrl);
  const parsed = parseJSON<{ plans: PlanDraft[] }>(text);
  return parsed.plans;
}

const HABIT_ACTION_TYPES: ActionDraft["actionType"][] = [
  "exercise",
  "diet",
  "meditation",
  "sleep",
  "custom",
];

const HABIT_INTENSITY_LEVELS: NonNullable<SubactionDraft["intensityLevel"]>[] = [
  "low",
  "medium",
  "high",
];

const VALID_PRACTICE_TYPES = new Set<number>(
  Object.values(PracticeType).filter((v): v is number => typeof v === "number")
);

const normalizeSubactionDraft = (payload: any, index: number): SubactionDraft => {
  const rawActionType =
    typeof payload?.actionType === "string"
      ? payload.actionType.trim().toLowerCase()
      : "";
  const actionType = (HABIT_ACTION_TYPES as string[]).includes(rawActionType)
    ? (rawActionType as SubactionDraft["actionType"])
    : "custom";
  const rawIntensity =
    typeof payload?.intensityLevel === "string"
      ? payload.intensityLevel.trim().toLowerCase()
      : "";
  const intensityLevel = (HABIT_INTENSITY_LEVELS as string[]).includes(rawIntensity)
    ? (rawIntensity as NonNullable<SubactionDraft["intensityLevel"]>)
    : undefined;
  const duration =
    typeof payload?.duration === "number" && Number.isFinite(payload.duration)
      ? payload.duration
      : undefined;
  const reflectionPrompt =
    typeof payload?.reflectionPrompt === "string" && payload.reflectionPrompt.trim()
      ? payload.reflectionPrompt.trim()
      : undefined;
  const aiPrompt =
    typeof payload?.aiPrompt === "string" && payload.aiPrompt.trim()
      ? payload.aiPrompt.trim()
      : undefined;

  return {
    name:
      typeof payload?.name === "string" && payload.name.trim()
        ? payload.name.trim()
        : `Subaction ${index + 1}`,
    description:
      typeof payload?.description === "string" && payload.description.trim()
        ? payload.description.trim()
        : "A practical subaction that supports the parent action.",
    actionType,
    ...(typeof duration === "number" ? { duration } : {}),
    ...(intensityLevel ? { intensityLevel } : {}),
    ...(reflectionPrompt ? { reflectionPrompt } : {}),
    ...(aiPrompt ? { aiPrompt } : {}),
  };
};

const normalizeActionDraft = (payload: any, index: number): ActionDraft => {
  const rawSubactions = Array.isArray(payload?.subactions) ? payload.subactions : [];
  const rawActionType =
    typeof payload?.actionType === "string"
      ? payload.actionType.trim().toLowerCase()
      : "";
  const actionType = (HABIT_ACTION_TYPES as string[]).includes(rawActionType)
    ? (rawActionType as ActionDraft["actionType"])
    : "custom";
  const duration =
    typeof payload?.duration === "number" && Number.isFinite(payload.duration)
      ? payload.duration
      : undefined;
  const goal =
    typeof payload?.goal === "string" && payload.goal.trim()
      ? payload.goal.trim()
      : undefined;

  return {
    name:
      typeof payload?.name === "string" && payload.name.trim()
        ? payload.name.trim()
        : `Action ${index + 1}`,
    description:
      typeof payload?.description === "string" && payload.description.trim()
        ? payload.description.trim()
        : "A practical action that advances the habit.",
    actionType,
    ...(typeof duration === "number" ? { duration } : {}),
    ...(goal ? { goal } : {}),
    subactions: rawSubactions.map((subaction: any, subactionIndex: number) =>
      normalizeSubactionDraft(subaction, subactionIndex)
    ),
  };
};

const normalizeHabitDraft = (payload: any): HabitDraft => {
  const rawActions = Array.isArray(payload?.actions) ? payload.actions : [];
  const actions = rawActions.map((action: any, actionIndex: number) =>
    normalizeActionDraft(action, actionIndex)
  );

  // Trust the AI's classification when it returns a valid PracticeType; fall
  // back to deriving one from the actions so it never lands on a bare default.
  const rawPracticeType =
    typeof payload?.practiceType === "number"
      ? payload.practiceType
      : typeof payload?.practiceType === "string" &&
          payload.practiceType.trim() &&
          !Number.isNaN(Number(payload.practiceType))
        ? Number(payload.practiceType)
        : undefined;
  const practiceType =
    rawPracticeType !== undefined && VALID_PRACTICE_TYPES.has(rawPracticeType)
      ? (rawPracticeType as PracticeType)
      : inferPracticeType(actions);

  return {
    habitName:
      typeof payload?.habitName === "string" && payload.habitName.trim()
        ? payload.habitName.trim()
        : "Habit",
    habitDescription:
      typeof payload?.habitDescription === "string" && payload.habitDescription.trim()
        ? payload.habitDescription.trim()
        : "A habit generated from the provided source material.",
    targetFrequency:
      typeof payload?.targetFrequency === "string" && payload.targetFrequency.trim()
        ? payload.targetFrequency.trim()
        : "Daily",
    practiceType,
    actions,
  };
};

const PLAN_LEARNING_STYLES: PlanDraftStep["learningStyle"][] = [
  "NARRATIVE",
  "CAUSAL",
  "SOCRATIC",
  "MODEL",
  "ACTION",
];

const CALLOUT_TONES: NonNullable<PlanDraftStepCallout["tone"]>[] = [
  "info",
  "warning",
  "tip",
  "note",
];

const normalizeStepLists = (payload: unknown): PlanDraftStepList[] => {
  if (!Array.isArray(payload)) return [];
  return payload
    .map((entry: any, index: number): PlanDraftStepList | null => {
      const rawItems = Array.isArray(entry?.items) ? entry.items : [];
      const items = rawItems
        .map((item: any) => (typeof item === "string" ? item.trim() : ""))
        .filter(Boolean);
      if (!items.length) return null;
      return {
        id:
          typeof entry?.id === "string" && entry.id.trim()
            ? entry.id.trim()
            : String(index + 1),
        ...(typeof entry?.title === "string" && entry.title.trim()
          ? { title: entry.title.trim() }
          : {}),
        ...(entry?.ordered === true ? { ordered: true } : {}),
        items,
      };
    })
    .filter((list): list is PlanDraftStepList => list !== null);
};

const normalizeStepCallouts = (payload: unknown): PlanDraftStepCallout[] => {
  if (!Array.isArray(payload)) return [];
  return payload
    .map((entry: any, index: number): PlanDraftStepCallout | null => {
      const text = typeof entry?.text === "string" ? entry.text.trim() : "";
      if (!text) return null;
      const rawTone = typeof entry?.tone === "string" ? entry.tone.trim().toLowerCase() : "";
      const tone = (CALLOUT_TONES as string[]).includes(rawTone)
        ? (rawTone as PlanDraftStepCallout["tone"])
        : undefined;
      return {
        id:
          typeof entry?.id === "string" && entry.id.trim()
            ? entry.id.trim()
            : String(index + 1),
        ...(tone ? { tone } : {}),
        ...(typeof entry?.title === "string" && entry.title.trim()
          ? { title: entry.title.trim() }
          : {}),
        text,
      };
    })
    .filter((callout): callout is PlanDraftStepCallout => callout !== null);
};

/**
 * Transcribe a PDF into clean verbatim text via Gemini. Replaces fragile
 * positional/heuristic extraction (pdf.js) — Gemini handles columns, ligatures,
 * reading order, headers/footers, and paragraph detection out of the box.
 *
 * Output contract: plain text only, no JSON, no markdown wrapping. Paragraphs
 * are separated by a single blank line; wrap-lines inside a paragraph are
 * joined into one continuous run.
 */
export async function extractPdfTextWithGemini(file: File): Promise<string> {
  const inlinePart = await fileToInlinePart(file);
  const instruction = `
You are transcribing a PDF document into plain text for downstream processing.

PRIMARY DIRECTIVE: Transcribe verbatim. Do not summarize, paraphrase, rewrite, translate, or "improve" the wording. Preserve the source's exact wording, capitalization, numbers, punctuation, and bullet markers.

Reading order:
- Read top to bottom in natural human reading order.
- For multi-column layouts, finish one column completely before moving to the next.
- Skip page-running headers, page-running footers, and standalone page numbers.

Paragraph and line handling:
- Join lines that the PDF wraps mid-paragraph back into a single continuous paragraph (no line break in the middle of a sentence).
- Separate distinct paragraphs with exactly one blank line.
- Section and chapter headings stay on their own line, with a blank line before and after.
- Preserve bulleted and numbered lists with each item on its own line, keeping the original bullet marker ("-", "•", "*", "1.", etc.).

Fix common PDF extraction artifacts:
- Ligature decompositions inside a word ("bene fi ts" → "benefits", "de fi nitely" → "definitely", "fi tness" → "fitness", "ﬂ" → "fl", etc.).
- Soft-hyphen line wraps ("wellbe-ing" or "wellbe- ing" → "wellbeing").
- Spurious double spaces inside a line.

Output format:
- Plain text only. No markdown fences, no JSON, no commentary, no labels like "Page 1" or "Transcription:".
- Start directly with the first line of body content.
`;

  return callGemini(
    [inlinePart, { text: instruction }],
    30000,
    false,
    null,
    { temperature: 0, disableFallback: true }
  );
}

// Normalize PDF text: collapse line-wraps within a paragraph into single spaces
// while preserving blank-line paragraph breaks ("\n\n+"). Heals soft-hyphen
// wraps ("wellbe-\ning" → "wellbeing") and the literal "\n" sometimes produced
// by JSON-escape mishaps.
export const flattenStepText = (raw: unknown): string => {
  if (typeof raw !== "string") return "";
  return raw
    .replace(/\r\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/(\w)-\n(\w)/g, "$1$2")
    .split(/\n{2,}/)
    .map((para) => para.replace(/[ \t\n]+/g, " ").trim())
    .filter(Boolean)
    .join("\n\n")
    .trim();
};

const normalizePlanDraft = (payload: any): PlanDraft => {
  const rawDayPlans = Array.isArray(payload?.dayPlans) ? payload.dayPlans : [];

  return {
    title:
      typeof payload?.title === "string" && payload.title.trim()
        ? payload.title.trim()
        : "Learning Plan",
    shortDescription:
      typeof payload?.shortDescription === "string" && payload.shortDescription.trim()
        ? payload.shortDescription.trim()
        : "A focused learning plan based on your source material.",
    description:
      typeof payload?.description === "string" && payload.description.trim()
        ? payload.description.trim()
        : "Structured guidance to help members apply the content step by step.",
    level:
      typeof payload?.level === "string" && payload.level.trim()
        ? payload.level.trim()
        : "Beginner",
    tags: sanitizeStringArray(payload?.tags),
    dayPlans: rawDayPlans.map((dayPlan: any, dayIndex: number) => {
      const rawSteps = Array.isArray(dayPlan?.steps) ? dayPlan.steps : [];

      return {
        title:
          typeof dayPlan?.title === "string" && dayPlan.title.trim()
            ? dayPlan.title.trim()
            : `Day ${dayIndex + 1}`,
        description:
          typeof dayPlan?.description === "string" && dayPlan.description.trim()
            ? dayPlan.description.trim()
            : "",
        steps: rawSteps.map((step: any, stepIndex: number) => {
          const rawLearningStyle =
            typeof step?.learningStyle === "string"
              ? step.learningStyle.trim().toUpperCase()
              : "";
          const learningStyle = (PLAN_LEARNING_STYLES as string[]).includes(rawLearningStyle)
            ? (rawLearningStyle as PlanDraftStep["learningStyle"])
            : "ACTION";

          const lists = normalizeStepLists(step?.lists);
          const callouts = normalizeStepCallouts(step?.callouts);
          const cleanedText = flattenStepText(step?.text);

          return {
            title:
              typeof step?.title === "string" && step.title.trim()
                ? step.title.trim()
                : `Step ${stepIndex + 1}`,
            subtitle:
              typeof step?.subtitle === "string" && step.subtitle.trim()
                ? step.subtitle.trim()
                : "",
            sectionTitle:
              typeof step?.sectionTitle === "string" && step.sectionTitle.trim()
                ? step.sectionTitle.trim()
                : "Core",
            description: flattenStepText(step?.description),
            position:
              typeof step?.position === "number" ? step.position : stepIndex + 1,
            learningStyle,
            ...(cleanedText ? { text: cleanedText } : {}),
            ...(lists.length ? { lists } : {}),
            ...(callouts.length ? { callouts } : {}),
          };
        }),
      };
    }),
  };
};

export type SingleLearningPlanRequest = {
  sources: ContentSources;
  planName?: string;
  authorName?: string;
  communityName?: string;
  additionalGuidance?: string;
};

export async function generateSingleLearningPlan({
  sources,
  planName,
  authorName,
  communityName,
  additionalGuidance,
}: SingleLearningPlanRequest): Promise<PlanDraft> {
  const context: string[] = [];
  if (planName?.trim()) context.push(`Plan name: ${planName.trim()}`);
  if (authorName?.trim()) context.push(`Author voice: ${authorName.trim()}`);
  if (communityName?.trim()) context.push(`Community: ${communityName.trim()}`);
  if (additionalGuidance?.trim()) context.push(`Extra guidance: ${additionalGuidance.trim()}`);

  const instruction = `
You are creating one learning plan for an online community based on the uploaded source material.

PRIMARY DIRECTIVE: Preserve the source content as closely as possible. Do not summarize or paraphrase aggressively. Reuse the source's wording, phrasing, examples, numbers, and dosages verbatim wherever practical. Only restructure where needed to fit the day/step skeleton.

Mapping rules:
- Treat each major chapter or top-level section of the source as one day plan (or two if a chapter is unusually long).
- Treat each sub-section as one step inside that day.
- Day count and steps-per-day must follow the source's natural structure, not a fixed target.
- If a plan name is provided in context, use it as the plan title; otherwise use the source's title.

Step content rules:
- Step "text" is the spine of the lesson and should hold the source's prose verbatim wherever possible.
- Extract every visible bulleted/numbered list in the source into the step's "lists" array as a structured list. Do NOT also include those list items as prose inside "text".
- Extract every highlighted note, warning, tip, or callout (anything visually emphasized, "❗", boxed notes, "IMPORTANT:" labels, side-bar tips) into the step's "callouts" array. Do NOT also include that callout content as prose inside "text".
- Lists and callouts live entirely in their own arrays. Give each list and callout a unique "id" inside the step starting at "1". Do not insert any marker tags into "text".

Context:
${context.length ? context.map((line) => `- ${line}`).join("\n") : "- No additional context provided"}

Return ONLY valid JSON (no markdown, no extra text):
{
  "plan": {
    "title": "Plan title",
    "shortDescription": "One sentence",
    "description": "2-3 sentences",
    "level": "Beginner",
    "tags": ["3-6 tags"],
    "dayPlans": [
      {
        "title": "Day title from the source chapter",
        "description": "What this day focuses on",
        "steps": [
          {
            "title": "Step title from the source sub-section",
            "subtitle": "Short subtitle if the source provides one",
            "sectionTitle": "Section",
            "description": "1-2 sentence summary",
            "text": "Source prose preserved verbatim.",
            "position": 1,
            "learningStyle": "ACTION",
            "lists": [
              {
                "id": "1",
                "title": "Optional heading shown above the list",
                "ordered": false,
                "items": ["Item one verbatim from source", "Item two verbatim from source"]
              }
            ],
            "callouts": [
              {
                "id": "1",
                "tone": "tip",
                "title": "Optional short label",
                "text": "The callout text preserved from the source"
              }
            ]
          }
        ]
      }
    ]
  }
}

Allowed callout tones: "info", "warning", "tip", "note". Omit "tone" if none clearly fits.
Omit "lists" and "callouts" entirely on steps that have none.
${LANGUAGE_RULE}
${JSON_RULES}`;

  const hasUrl = !!sources.websiteUrl;
  const parts = await buildParts(sources, instruction);
  const text = await callGemini(parts, 18000, hasUrl);
  const parsed = parseJSON<any>(text);

  const rawPlan = Array.isArray(parsed)
    ? parsed[0]
    : parsed?.plan ?? (Array.isArray(parsed?.plans) ? parsed.plans[0] : parsed);

  if (!rawPlan || typeof rawPlan !== "object") {
    throw new Error("AI did not return a valid learning plan.");
  }

  const normalizedPlan = normalizePlanDraft(rawPlan);
  if (!normalizedPlan.dayPlans.length) {
    throw new Error("Generated plan is missing day plans.");
  }

  return normalizedPlan;
}

export type SingleHabitRequest = {
  sources: ContentSources;
  habitName?: string;
  authorName?: string;
  communityName?: string;
  additionalGuidance?: string;
};

export async function generateSingleHabit({
  sources,
  habitName,
  authorName,
  communityName,
  additionalGuidance,
}: SingleHabitRequest): Promise<HabitDraft> {
  const context: string[] = [];
  if (habitName?.trim()) context.push(`Habit name: ${habitName.trim()}`);
  if (authorName?.trim()) context.push(`Author voice: ${authorName.trim()}`);
  if (communityName?.trim()) context.push(`Community: ${communityName.trim()}`);
  if (additionalGuidance?.trim()) context.push(`Extra guidance: ${additionalGuidance.trim()}`);

  const instruction = `
You are creating one habit for an online community.

Use the uploaded sources above as your primary grounding. If the source is narrow, stay narrow and practical.
Generate exactly one habit, not multiple.

Requirements:
- If a habit name is provided in context, use it as the habit title.
- Build 3-7 actions by default unless the source clearly supports fewer.
- Each action should include 2-5 subactions when the material supports it.
- actionType must be one of: "exercise", "diet", "meditation", "sleep", "custom".
- practiceType is an integer classifying the whole habit (keep it consistent with the actions): 1=meditation, 2=diet, 3=exercise, 5=fasting, 6=custom, 7=hydration, 8=focus/deep work, 11=regulation toolkit, 4=default (only when none fit).
- reflectionPrompt is optional. Only include it when an AI chat would genuinely help, and if included it must start with "You are ...".
- Keep the habit grounded in the source material and make it practical for real users.

Context:
${context.length ? context.map((line) => `- ${line}`).join("\n") : "- No additional context provided"}

Return ONLY valid JSON (no markdown, no extra text):
{
  "habit": {
    "habitName": "Habit name",
    "habitDescription": "2-3 sentences",
    "targetFrequency": "Daily",
    "practiceType": 3,
    "actions": [
      {
        "name": "Action name",
        "description": "1-2 sentence summary",
        "actionType": "custom",
        "duration": 15,
        "goal": "What this action helps the user achieve",
        "subactions": [
          {
            "name": "Subaction name",
            "description": "1-2 sentence summary",
            "actionType": "custom",
            "duration": 5,
            "intensityLevel": "low",
            "reflectionPrompt": "You are ..."
          }
        ]
      }
    ]
  }
}
${LANGUAGE_RULE}
${JSON_RULES}`;

  const hasUrl = !!sources.websiteUrl;
  const parts = await buildParts(sources, instruction);
  const text = await callGemini(parts, 12000, hasUrl);
  const parsed = parseJSON<any>(text);

  const rawHabit = Array.isArray(parsed)
    ? parsed[0]
    : parsed?.habit ?? (Array.isArray(parsed?.habits) ? parsed.habits[0] : parsed);

  if (!rawHabit || typeof rawHabit !== "object") {
    throw new Error("AI did not return a valid habit.");
  }

  const normalizedHabit = normalizeHabitDraft(rawHabit);
  if (!normalizedHabit.actions.length) {
    throw new Error("Generated habit is missing actions.");
  }

  return normalizedHabit;
}

export async function generateFullCommunityContent(
  sources: ContentSources
): Promise<FullCommunityContent> {
  const [communityAndAuthor, habitsAndQuotes, plans, questionnaire] = await Promise.all([
    generateCommunityAndAuthor(sources),
    generateHabitsAndQuotes(sources),
    generateLearningPlans(sources),
    generateQuestionnaire(sources),
  ]);

  return {
    community: communityAndAuthor.community,
    author: communityAndAuthor.author,
    habits: habitsAndQuotes.habits,
    quotes: habitsAndQuotes.quotes,
    plans,
    questionnaire,
  };
}

// ─── Chat-based draft editor ──────────────────────────────────────────────────

export type DraftEditableFields = {
  title?: string;
  description?: string;
  shortDescription?: string;
  categoryName?: string;
  tags?: string[];
  writingStyle?: string;
  visualStyle?: string;
  authorDescription?: string;
  aiGuidelines?: string;
  primaryColor?: string;
  secondaryColor?: string;
};

export type ChatEditResult = {
  changes: DraftEditableFields;
  summary: string;
};

export async function editDraftWithChat(
  current: DraftEditableFields & { authorName?: string },
  userMessage: string
): Promise<ChatEditResult> {
  const categoriesList = COMMUNITY_CATEGORIES.join(", ");

  const prompt = `You are a community profile editor. A creator is reviewing their AI-generated community profile and wants to make changes.

CURRENT PROFILE:
- Title: ${current.title || ""}
- Short description: ${current.shortDescription || ""}
- Full description: ${current.description || ""}
- Category: ${current.categoryName || ""} (available: ${categoriesList})
- Tags: ${(current.tags ?? []).join(", ")}
- Primary color: ${current.primaryColor || "not set"}
- Secondary color: ${current.secondaryColor || "not set"}
- Writing style: ${current.writingStyle || ""}
- Visual style: ${current.visualStyle || ""}
- Author name: ${current.authorName || ""}
- Author bio: ${current.authorDescription || ""}
- AI guidelines: ${current.aiGuidelines || ""}

CREATOR'S REQUEST:
"${userMessage}"

Apply the requested changes. Return ONLY valid JSON:
{
  "changes": {
    // include ONLY fields that actually change — omit unchanged fields
    // tags must be an array of strings
    // colors must be plain 6-digit hex codes starting with # and must not include descriptions
  },
  "summary": "One short sentence describing what was changed"
}
${JSON_RULES}`;

  const text = await callGemini([{ text: prompt }], 1200);
  return parseJSON<ChatEditResult>(text);
}

// ─── Legacy function (kept for backward compatibility) ────────────────────────

export async function generateCommunityDraft(
  data: OnboardingData
): Promise<GeneratedDraft> {
  const prompt = `You are helping a creator set up their community platform. Based on the following information, generate a polished community profile.

COMMUNITY INFORMATION:
- Name: ${data.communityName}
- Category: ${data.category}
- Description: ${data.communityDescription}

TARGET AUDIENCE:
- Ideal member: ${data.targetAudience}
- Their biggest challenge: ${data.mainChallenge}
- Transformation offered: ${data.transformation}

CONTENT SOURCES:
- Website: ${data.websiteUrl || "Not provided"}
- Social links: ${data.socialLinks || "Not provided"}
- Additional context: ${data.additionalContext || "None"}

STYLE PREFERENCES:
- Writing style: ${data.writingStyle}
- Visual style: ${data.visualStyle}

Return ONLY valid JSON (no markdown, no extra text):
{
  "title": "Community name",
  "description": "Full description, 2-3 sentences",
  "shortDescription": "One punchy sentence under 15 words",
  "categoryName": "Best fitting category name",
  "tags": ["5-7 relevant tags"],
  "writingStyle": "Detailed writing style guide, 2-3 sentences",
  "visualStyle": "Detailed visual style guide, 2-3 sentences",
  "authorDescription": "A 2-sentence bio in 3rd person",
  "aiGuidelines": "Specific AI content guidelines, 3-4 sentences"
}`;

  const text = await callGemini([{ text: prompt }], 1500);
  return parseJSON<GeneratedDraft>(text);
}

// Workout program ingestion moved to ../plan/components/workoutProgramApi.ts

// ─── ElevenLabs knowledge base (voice agent grounding) ────────────────────────
//
// Mirrors the direct-fetch pattern used by createCommunityAgent above. Uploads a
// document to the ElevenLabs Conversational AI knowledge base, computes its RAG
// index, and attaches it to an existing agent with RAG enabled so the voice
// assistant answers grounded in the author's own material.

export type ElevenLabsKbDoc = {
  id: string;
  name: string;
};

// ElevenLabs model used to build the retrieval index for a KB document.
const ELEVENLABS_RAG_MODEL = "e5_mistral_7b_instruct";

function elevenLabsHeaders(extra?: Record<string, string>): Record<string, string> {
  const apiKey = (DEFAULT_ELEVENLABS_API_KEY || "").trim();
  if (!apiKey) {
    throw new Error("ElevenLabs API key is not configured.");
  }
  return { "xi-api-key": apiKey, ...(extra || {}) };
}

/** Upload one file to the ElevenLabs knowledge base. Returns the document id + name. */
export async function createElevenLabsKbFromFile({
  file,
  name,
}: {
  file: File;
  name?: string;
}): Promise<ElevenLabsKbDoc> {
  if (!file || file.size <= 0) {
    throw new Error("Knowledge base upload requires a non-empty file.");
  }

  const formData = new FormData();
  formData.append("file", file, file.name);
  if (name?.trim()) formData.append("name", name.trim());

  const response = await fetch(`${ELEVENLABS_BASE}/convai/knowledge-base/file`, {
    method: "POST",
    headers: elevenLabsHeaders(),
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`ElevenLabs KB upload failed (${response.status}): ${errorText}`);
  }

  const json = await response.json();
  const id = json?.id;
  if (!id || typeof id !== "string") {
    throw new Error("ElevenLabs did not return an id for the knowledge base document.");
  }
  return { id, name: json?.name || name || file.name };
}

/** Kick off RAG indexing. ElevenLabs completes large indexes asynchronously. */
export async function computeElevenLabsRagIndex(docId: string): Promise<void> {
  const url = `${ELEVENLABS_BASE}/convai/knowledge-base/${docId}/rag-index`;
  const response = await fetch(url, {
    method: "POST",
    headers: elevenLabsHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ model: ELEVENLABS_RAG_MODEL }),
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`ElevenLabs RAG index failed (${response.status}): ${errorText}`);
  }
}

/** Fetch the agent's current prompt config so we can merge knowledge base entries. */
async function getElevenLabsAgentPrompt(agentId: string): Promise<any> {
  const response = await fetch(`${ELEVENLABS_BASE}/convai/agents/${agentId}`, {
    method: "GET",
    headers: elevenLabsHeaders(),
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`ElevenLabs get agent failed (${response.status}): ${errorText}`);
  }
  const json = await response.json();
  return json?.conversation_config?.agent?.prompt ?? {};
}

async function patchElevenLabsAgentPrompt(agentId: string, prompt: any): Promise<void> {
  const response = await fetch(`${ELEVENLABS_BASE}/convai/agents/${agentId}`, {
    method: "PATCH",
    headers: elevenLabsHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      conversation_config: { agent: { prompt } },
    }),
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`ElevenLabs agent update failed (${response.status}): ${errorText}`);
  }
}

/**
 * Attach a KB document to an agent (RAG, usage_mode "auto") without dropping any
 * documents already attached. Enables RAG on the agent prompt.
 */
export async function attachKnowledgeToAgent({
  agentId,
  doc,
}: {
  agentId: string;
  doc: ElevenLabsKbDoc;
}): Promise<void> {
  const prompt = await getElevenLabsAgentPrompt(agentId);
  const existing: any[] = Array.isArray(prompt.knowledge_base)
    ? prompt.knowledge_base
    : [];
  if (existing.some((entry) => entry?.id === doc.id)) return;

  const knowledge_base = [
    ...existing,
    { type: "file", name: doc.name, id: doc.id, usage_mode: "auto" },
  ];

  await patchElevenLabsAgentPrompt(agentId, {
    ...prompt,
    knowledge_base,
    rag: { ...(prompt.rag || {}), enabled: true },
  });
}

/** Remove a KB document from an agent's knowledge base. */
export async function detachKnowledgeFromAgent({
  agentId,
  docId,
}: {
  agentId: string;
  docId: string;
}): Promise<void> {
  const prompt = await getElevenLabsAgentPrompt(agentId);
  const existing: any[] = Array.isArray(prompt.knowledge_base)
    ? prompt.knowledge_base
    : [];
  const knowledge_base = existing.filter((entry) => entry?.id !== docId);
  if (knowledge_base.length === existing.length) return;
  await patchElevenLabsAgentPrompt(agentId, { ...prompt, knowledge_base });
}

/** Delete a document from the ElevenLabs knowledge base entirely. */
export async function deleteElevenLabsKbDoc(docId: string): Promise<void> {
  const response = await fetch(
    `${ELEVENLABS_BASE}/convai/knowledge-base/${docId}?force=true`,
    { method: "DELETE", headers: elevenLabsHeaders() }
  );
  if (!response.ok && response.status !== 404) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(
      `ElevenLabs KB delete failed (${response.status}): ${errorText}`
    );
  }
}
